import { use, useMemo } from "react";
import { GQL, Utils } from "@binale-tech/shared";
import type { GenericRecord } from "@domain/models/GenericRecord";
import { GenericRecordUtils } from "@domain/models/utils/GenericRecordUtils";
import { type PaymentPrototype } from "@domain/models/Payment";
import { UserContext } from "@domain/context/UserProvider";
import {
    genericRecordDocuments,
    genericRecordSave,
    genericRecordsCancel,
    genericRecordsColor,
    genericRecordsConfirm,
    genericRecordsDelete,
    genericRecordsImport,
    genericRecordsJournal,
    genericRecordsSetPriorities,
} from "@domain/context/mutations/genericRecordMutations.graphql";
import { logger } from "@domain/infrastructure/logger";
import { useMutation } from "@apollo/client/react";
import { useToastSaver } from "@ui-components/hooks/useToastSaver";

export interface ImportManyOptions {
    /** Import every record as a draft. */
    draft?: boolean;
    /** The caller owns the toast: no toast here, and a failure rejects instead of resolving to undefined. */
    silent?: boolean;
}

export const useRecordsSaver = () => {
    const { selectedCompany: companyId } = use(UserContext);
    const toastSaver = useToastSaver();

    const [saveMutation] = useMutation(genericRecordSave);
    const [documentsMutation] = useMutation(genericRecordDocuments);
    const [deleteMutation] = useMutation(genericRecordsDelete);
    const [cancelMutation] = useMutation(genericRecordsCancel);
    const [confirmMutation] = useMutation(genericRecordsConfirm);
    const [journalMutation] = useMutation(genericRecordsJournal);
    const [colorMutation] = useMutation(genericRecordsColor);
    const [setPrioritiesMutation] = useMutation(genericRecordsSetPriorities);
    const [importMutation] = useMutation(genericRecordsImport);

    return useMemo(() => {
        const saveRecord = async (
            record: GenericRecord,
            payment: PaymentPrototype,
            product: GQL.IProductKey,
            opts?: { groupId?: string; isTemplate?: boolean; aiSuggestion?: string },
        ) => {
            logger.log("save record: ", { record, payment, companyId, product });
            const getPayment = (): GQL.IPaymentProtoInput => {
                if (!payment) {
                    return null;
                }
                if (!Utils.PaymentUtils.isProductPaymentRepresentation(product)) {
                    throw new Error(
                        `only payment representation record save must pass 'payment' on save, received it in ${product}`,
                    );
                }
                return {
                    sourceRecordKey: payment.sourceRecordKey,
                    strategy: GQL.IPaymentProtoStrategy.Representation,
                    strategyRepresentation: {
                        discountAmount: payment.skontoBetrag,
                        representationRecordKey: record.key,
                    },
                };
            };
            return toastSaver(() =>
                saveMutation({
                    variables: {
                        input: {
                            companyId,
                            product,
                            record: GenericRecordUtils.mapToGQL(record),
                            payment: getPayment(),
                            groupId: opts?.groupId,
                            isTemplate: opts?.isTemplate,
                            aiSuggestion: opts?.aiSuggestion,
                        },
                    },
                }).then((res) => res.data?.genericRecordSave),
            );
        };

        return {
            save: saveRecord,
            saveDocuments: async (id: string, product: GQL.IProductKey, documents: GQL.IEntityDocumentId[]) => {
                return toastSaver(() =>
                    documentsMutation({
                        variables: {
                            input: {
                                companyId,
                                product,
                                id,
                                documents: documents.map(({ id }) => ({ id })),
                            },
                        },
                    }).then((res) => res.data?.genericRecordDocuments),
                );
            },
            deleteByKeys: async (ids: string[], product: GQL.IProductKey, opts?: { isTemplate?: boolean }) => {
                logger.log("delete record", { ids, companyId, product });
                return toastSaver(() =>
                    deleteMutation({
                        variables: {
                            input: {
                                companyId,
                                product,
                                ids,
                                isTemplate: opts?.isTemplate,
                            },
                        },
                    }).then((res) => res.data?.genericRecordsDelete),
                );
            },
            cancelByKeys: async (ids: string[], product: GQL.IProductKey) => {
                logger.log("cancel record", { ids, companyId, product });
                return toastSaver(() =>
                    cancelMutation({
                        variables: {
                            input: {
                                companyId,
                                product,
                                ids,
                            },
                        },
                    }).then((res) => res.data?.genericRecordsCancel),
                );
            },
            confirm: async (ids: string[], product: GQL.IProductKey) => {
                logger.log("confirm record", { ids, companyId, product });
                await toastSaver(() =>
                    confirmMutation({
                        variables: {
                            input: {
                                companyId,
                                product,
                                ids,
                            },
                        },
                    }),
                );
            },
            journal: async (ids: string[], product: GQL.IProductKey) => {
                logger.log("journal record", { ids, companyId, product });
                await toastSaver(() =>
                    journalMutation({
                        variables: {
                            input: {
                                companyId,
                                product,
                                ids,
                            },
                        },
                    }),
                );
            },
            color: async (ids: string[], product: GQL.IProductKey, color: GQL.IDocumentColors | null) => {
                logger.log("color record", { ids, companyId, product, color });
                await toastSaver(() =>
                    colorMutation({
                        variables: {
                            input: {
                                companyId,
                                product,
                                ids,
                            },
                            color,
                        },
                    }),
                );
            },
            setPriorities: async (priorities: Record<string, number>, product: GQL.IProductKey, groupId: string) => {
                logger.log("setPriorities", { priorities, companyId, product, groupId });
                await toastSaver(() =>
                    setPrioritiesMutation({
                        variables: {
                            input: {
                                companyId,
                                product,
                                groupId,
                                ids: Object.keys(priorities),
                                priorities: Object.values(priorities),
                            },
                        },
                    }),
                );
            },
            importMany: async (
                records: GenericRecord[],
                product: GQL.IProductKey,
                groupId?: string,
                opts?: ImportManyOptions,
            ) => {
                records = records.filter(Boolean);
                if (records.length === 0) {
                    return;
                }
                const runImport = () =>
                    importMutation({
                        variables: {
                            input: {
                                companyId,
                                product,
                                groupId,
                                draft: opts?.draft,
                                records: records.map(GenericRecordUtils.mapToGQL),
                            },
                        },
                    });
                if (opts?.silent) {
                    return runImport();
                }
                // Return the toastSaver result so callers can tell success from failure:
                // toastSaver swallows errors (shows a danger toast and resolves to
                // undefined), so a discarded result hides failed imports from callers
                // that must act on the outcome (e.g. split = import-then-delete-original).
                return toastSaver(runImport);
            },
        };
    }, [
        companyId,
        toastSaver,
        saveMutation,
        documentsMutation,
        deleteMutation,
        cancelMutation,
        confirmMutation,
        journalMutation,
        colorMutation,
        setPrioritiesMutation,
        importMutation,
    ]);
};
